import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Header, BottomNav } from "../components/Header";
import { Button } from "../components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Edit, Trash2, Package, Gavel, ShoppingCart, CheckCircle, History, Loader2 } from "lucide-react";

const AdminProductsPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteProduct, setDeleteProduct] = useState(null);
  const [historyProduct, setHistoryProduct] = useState(null);
  const [historyEntries, setHistoryEntries] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    if (user && token) {
      console.log("Fetching products with token:", token?.substring(0, 20) + "...");
      fetchMyProducts();
    }
  }, [user, token]);

  const fetchMyProducts = async () => {
    if (!token) {
      console.error("No token available");
      return;
    }
    try {
      // Get all admin products including sold and cancelled ones
      console.log("Calling /admin/all-products...");
      const response = await axios.get(`${API}/admin/all-products`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      console.log("Products loaded:", response.data?.length);
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
      const errorMsg = error.response?.data?.detail || error.message || "Error desconocido";
      console.error("Error detail:", errorMsg, "Status:", error.response?.status);
      toast.error(`Error al cargar productos: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRepublishEdit = (product) => {
    // Navigate to create page with product data for editing
    navigate('/create', { state: { republishProduct: product } });
  };

  const handleMarkAsSold = async (product) => {
    if (!window.confirm("¿Marcar este producto como vendido?")) return;
    const productId = product.product_id;
    const price = product.sale_type === "auction" 
      ? (product.current_bid || product.price) 
      : product.price;
    
    try {
      await axios.put(`${API}/products/${productId}`, { 
        is_sold: true, 
        paid: true,
        status: "sold",
        final_price: price
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("Producto marcado como vendido");
      // Update local state immediately for better UX
      setProducts(prev => prev.map(p => 
        p.product_id === productId 
          ? { ...p, is_sold: true, paid: true, status: "sold", final_price: price }
          : p
      ));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al marcar como vendido");
    }
  };

  const handleDelete = async () => {
    if (!deleteProduct) return;

    try {
      await axios.delete(`${API}/products/${deleteProduct.product_id}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      
      toast.success("Producto eliminado");
      setDeleteProduct(null);
      fetchMyProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar");
    }
  };

  const openHistory = async (product) => {
    setHistoryProduct(product);
    setHistoryLoading(true);
    try {
      const response = await axios.get(`${API}/products/${product.product_id}/edit-history`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setHistoryEntries(response.data.entries || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al cargar el historial");
      setHistoryEntries([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] pb-20 md:pb-8">
      <Header />

      <main className="max-w-4xl mx-auto px-4 py-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#64748B] hover:text-[#0F172A] mb-6"
          data-testid="back-button"
        >
          <ArrowLeft size={20} />
          Volver
        </button>

        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-[#0F172A]">Administrar Productos</h1>
          <Button
            onClick={() => navigate("/create")}
            className="rounded-full bg-[#475569] hover:bg-[#334155]"
          >
            + Nuevo Producto
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner"></div>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-12">
            <Package size={64} className="mx-auto text-[#CBD5E1] mb-4" />
            <h3 className="text-lg font-semibold text-[#0F172A]">No tienes productos</h3>
            <p className="text-[#64748B] mb-4">Comienza publicando tu primer producto</p>
            <Button
              onClick={() => navigate("/create")}
              className="rounded-full bg-[#475569] hover:bg-[#334155]"
            >
              Publicar Producto
            </Button>
          </div>
        ) : (
          <div className="space-y-4" data-testid="admin-products-list">
            {products.map((product) => (
              <div
                key={product.product_id}
                className="bg-white rounded-xl border border-[#E2E8F0] p-4 flex gap-4"
                data-testid={`admin-product-${product.product_id}`}
              >
                {/* Image */}
                <div className="w-24 h-24 rounded-lg overflow-hidden bg-[#F1F5F9] flex-shrink-0">
                  <img
                    src={product.image_url}
                    alt={product.title}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-[#0F172A] line-clamp-1">{product.title}</h3>
                      <p className="text-sm text-[#64748B] line-clamp-1 mt-1">{product.description}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs flex items-center gap-1 flex-shrink-0 ${
                      product.sale_type === "auction" ? "badge-auction" : "badge-direct"
                    }`}>
                      {product.sale_type === "auction" ? (
                        <><Gavel size={10} /> Subasta</>
                      ) : (
                        <><ShoppingCart size={10} /> Venta</>
                      )}
                    </span>
                  </div>

                  <div className="mt-3">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs text-[#64748B]">
                        {product.sale_type === "auction" ? "Oferta actual" : "Precio"}
                      </span>
                      <span className="text-lg font-bold text-[#10B981]">
                        ${(product.sale_type === "auction" ? product.current_bid : product.price)?.toLocaleString()}
                      </span>
                      {product.has_discount && (
                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                          Cupón: {product.discount_percent}% off
                        </span>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      {product.is_sold ? (
                        <>
                          <span className="px-3 py-1.5 bg-[#10B981]/10 text-[#10B981] rounded-full text-sm font-medium inline-flex items-center gap-1">
                            <CheckCircle size={14} />
                            Vendido
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-full h-8 px-3 text-xs"
                            onClick={() => navigate(`/edit/${product.product_id}`)}
                            data-testid={`edit-${product.product_id}`}
                          >
                            <Edit size={14} className="mr-1" />
                            Editar / Relistar
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-full h-8 px-3 text-xs"
                            onClick={() => openHistory(product)}
                            data-testid={`history-${product.product_id}`}
                          >
                            <History size={14} className="mr-1" />
                            Historial
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-full h-8 px-3 text-xs"
                            onClick={() => navigate(`/edit/${product.product_id}`)}
                            data-testid={`edit-${product.product_id}`}
                          >
                            <Edit size={14} className="mr-1" />
                            Editar
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-full h-8 px-3 text-xs"
                            onClick={() => openHistory(product)}
                            data-testid={`history-${product.product_id}`}
                          >
                            <History size={14} className="mr-1" />
                            Historial
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-full h-8 px-3 text-xs border-green-500 text-green-600 hover:bg-green-50"
                            onClick={() => handleMarkAsSold(product)}
                            data-testid={`mark-sold-${product.product_id}`}
                          >
                            <CheckCircle size={14} className="mr-1" />
                            Vendido
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-full h-8 w-8 p-0 border-red-300 text-red-500 hover:bg-red-50"
                            onClick={() => setDeleteProduct(product)}
                            data-testid={`delete-${product.product_id}`}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteProduct} onOpenChange={(open) => !open && setDeleteProduct(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar producto?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. El producto "{deleteProduct?.title}" será eliminado permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="rounded-full bg-red-600 hover:bg-red-700"
              onClick={handleDelete}
              data-testid="confirm-delete-btn"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit History Dialog */}
      <Dialog open={!!historyProduct} onOpenChange={(open) => !open && setHistoryProduct(null)}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto" data-testid="edit-history-dialog">
          <DialogHeader>
            <DialogTitle>Historial de Ediciones</DialogTitle>
            <DialogDescription>
              {historyProduct?.title}
            </DialogDescription>
          </DialogHeader>
          {historyLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="animate-spin" size={24} />
            </div>
          ) : historyEntries.length === 0 ? (
            <p className="text-sm text-[#64748B] py-6 text-center" data-testid="edit-history-empty">
              Este producto todavía no tiene cambios registrados.
            </p>
          ) : (
            <div className="space-y-3" data-testid="edit-history-list">
              {historyEntries.map((entry) => (
                <div
                  key={entry.history_id}
                  className="p-3 rounded-lg border border-[#E2E8F0] bg-[#FAFAF9]"
                  data-testid={`edit-history-entry-${entry.history_id}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-[#0F172A]">{entry.editor_name}</span>
                    <span className="text-xs text-[#94A3B8]">
                      {new Date(entry.created_at).toLocaleString("es-PA", { dateStyle: "short", timeStyle: "short" })}
                    </span>
                  </div>
                  <ul className="space-y-1">
                    {entry.changes.map((change, idx) => (
                      <li key={idx} className="text-xs text-[#334155]">
                        <span className="font-medium">{change.field_label}:</span>{" "}
                        <span className="text-[#94A3B8] line-through">{change.old_display}</span>{" "}
                        →{" "}
                        <span className="text-[#0F172A] font-medium">{change.new_display}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <BottomNav />
    </div>
  );
};

export default AdminProductsPage;
